import { Box, Text, Tooltip, rem } from '@mantine/core';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { TABLE_PRIMITIVE_VALUE_PLACEHOLDER } from './TablePrimitiveValue.utils';

type TableEntityIcon = IconDefinition | ReactElement;

type BaseProps = {
  icon?: TableEntityIcon;
  placeholder?: string;
  tooltip?: ReactNode;
};

type SimpleEntityBaseProps = BaseProps & {
  value: string | null | undefined;
};

type SimpleEntityProps =
  | (SimpleEntityBaseProps & {
      type: 'bankAccount';
    })
  | (SimpleEntityBaseProps & {
      type: 'connection';
    })
  | (SimpleEntityBaseProps & {
      type: 'contact';
    })
  | (SimpleEntityBaseProps & {
      type: 'listing';
    })
  | (SimpleEntityBaseProps & {
      type: 'owner';
    });

type ReservationEntityValue = {
  dateRange?: string | null | undefined;
  title: string | null | undefined;
};

type ReservationEntityProps = BaseProps & {
  type: 'reservation';
  value: ReservationEntityValue | null | undefined;
};

type TransactionEntityProps = BaseProps & {
  type: 'transaction';
  value: string | null | undefined;
};

export type TableEntityValueProps =
  | ReservationEntityProps
  | SimpleEntityProps
  | TransactionEntityProps;

const iconBaseStyle: CSSProperties = {
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  height: rem(12),
  justifyContent: 'center',
  width: rem(12),
};

const transactionIconBaseStyle: CSSProperties = {
  ...iconBaseStyle,
  height: rem(14),
  width: rem(14),
};

const surfaceBaseStyle: CSSProperties = {
  alignSelf: 'center',
  alignItems: 'center',
  backgroundColor: '#FFFFFF',
  border: '0.5px solid rgba(0, 0, 0, 0.15)',
  borderRadius: rem(4),
  boxShadow: '0px 1px 1px 0px rgba(0, 0, 0, 0.06)',
  display: 'inline-flex',
  gap: rem(4),
  height: rem(24),
  maxWidth: '100%',
  paddingBlock: 0,
  paddingInline: rem(6),
  width: 'fit-content',
};

const floatingSurfaceStyle: CSSProperties = {
  background: '#FFFFFF',
  border: '1px solid #FAFAFA',
  borderRadius: rem(6),
  boxShadow:
    '0px 0px 1px 0px rgba(0, 0, 0, 0.2), 0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 8px 30px 0px rgba(0, 0, 0, 0.1)',
};

const EntityLabel = ({
  children,
  color,
  fontSize = 12,
  letterSpacing = '0.01px',
}: {
  children: ReactNode;
  color: string;
  fontSize?: number;
  letterSpacing?: string;
}) => (
  <Text
    c={color}
    fw={500}
    fz={fontSize}
    lh={1.4}
    style={{
      letterSpacing,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Text>
);

const EntityMeta = ({ children }: { children: ReactNode }) => (
  <Text
    c="#737373"
    fw={400}
    fz={8}
    lh={1.4}
    style={{
      letterSpacing: '0.1px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Text>
);

const AtEntityIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="12"
    viewBox="0 0 11 11"
    width="12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 5.5C8 6.88071 6.88071 8 5.5 8C4.11929 8 3 6.88071 3 5.5C3 4.11929 4.11929 3 5.5 3C6.88071 3 8 4.11929 8 5.5ZM8 5.5V3.5M8 5.5L8.00001 6.47238C8 7 8.25 7.75 9 7.75C10 7.75 10.5 6.60063 10.5 5.5C10.5 2.73858 8.26142 0.5 5.5 0.5C2.73858 0.5 0.5 2.73858 0.5 5.5C0.5 8.26142 2.73858 10.5 5.5 10.5C6.29825 10.5 7.0528 10.3129 7.72222 9.98027"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BankEntityIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="12"
    viewBox="0 0 12 12"
    width="12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.4882 2.55413C10.7999 2.69573 11 3.00647 11 3.34881C11 3.83087 10.6092 4.22165 10.1272 4.22165H1.37284C0.890782 4.22165 0.5 3.83087 0.5 3.34881C0.5 3.00647 0.700135 2.69573 1.01183 2.55413L5.33639 0.589544C5.59921 0.470152 5.90079 0.470152 6.16361 0.589544L10.4882 2.55413Z"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
    <path
      d="M10.045 8.99165H1.455C0.927568 8.99165 0.5 9.41922 0.5 9.94665C0.5 10.4741 0.927568 10.9016 1.455 10.9016H10.045C10.5724 10.9016 11 10.4741 11 9.94665C11 9.41922 10.5724 8.99165 10.045 8.99165Z"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
    <path
      d="M1.455 4.22165V8.99165"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
    <path
      d="M4.32 4.22165V8.99165"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
    <path
      d="M7.18 4.22165V8.99165"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
    <path
      d="M10.045 4.22165V8.99165"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
  </svg>
);

const getEntityIconDefinition = (
  type: TableEntityValueProps['type']
): TableEntityIcon => {
  switch (type) {
    case 'listing':
      return 'HomeIcon';
    case 'owner':
      return 'UserIcon';
    case 'transaction':
      return 'ShareIcon';
    case 'reservation':
      return 'CalendarEventIcon';
    case 'contact':
      return <AtEntityIcon />;
    case 'bankAccount':
      return <BankEntityIcon />;
    default:
      // Connections can pass a real app-logo element via `icon`; otherwise use the generic fallback.
      return 'Globe2Icon';
  }
};

const getEntityIconColor = (type: TableEntityValueProps['type']) => {
  switch (type) {
    case 'listing':
    case 'contact':
    case 'connection':
    case 'bankAccount':
    case 'transaction':
      return '#1343CC';
    case 'owner':
    case 'reservation':
      return '#16A34A';
    default:
      return '#1343CC';
  }
};

const renderIcon = ({
  color,
  icon,
  style = iconBaseStyle,
  testId,
}: {
  color: string;
  icon: TableEntityIcon;
  style?: CSSProperties;
  testId: string;
}) => {
  if (typeof icon === 'string') {
    return (
      <Icon
        color={color}
        data-testid={testId}
        icon={icon}
        size={12}
        strokeWidth={1.5}
      />
    );
  }

  return (
    <Box data-testid={testId} style={{ ...style, color }}>
      {icon}
    </Box>
  );
};

const wrapWithTooltip = (content: ReactNode, tooltip?: ReactNode) => {
  if (!tooltip) return content;

  return (
    <Tooltip
      disabled={!tooltip}
      label={tooltip}
      maw={400}
      multiline
      offset={6}
      position="top"
      styles={{
        tooltip: {
          ...floatingSurfaceStyle,
          color: '#171717',
          fontSize: rem(13),
          fontWeight: 500,
          lineHeight: 1.4,
          padding: rem(8),
        },
      }}
      withArrow={false}
      withinPortal
    >
      <Box
        style={{
          display: 'inline-flex',
          maxWidth: '100%',
          width: 'fit-content',
        }}
      >
        {content}
      </Box>
    </Tooltip>
  );
};

const Placeholder = ({ value }: { value: string }) => (
  <Text c="#737373" fw={400} fz={13} lh={1.4}>
    {value}
  </Text>
);

const SimpleEntity = ({
  icon,
  placeholder,
  tooltip,
  type,
  value,
}: SimpleEntityProps) => {
  const resolvedValue = value?.trim();

  if (!resolvedValue) {
    return (
      <Placeholder value={placeholder || TABLE_PRIMITIVE_VALUE_PLACEHOLDER} />
    );
  }

  const resolvedIconColor = getEntityIconColor(type);
  const resolvedIcon = icon || getEntityIconDefinition(type);

  return wrapWithTooltip(
    <Box style={surfaceBaseStyle}>
      <Box style={iconBaseStyle}>
        {renderIcon({
          color: resolvedIconColor,
          icon: resolvedIcon,
          testId: `table-entity-${type}-icon`,
        })}
      </Box>
      <EntityLabel color="#525252">{resolvedValue}</EntityLabel>
    </Box>,
    tooltip
  );
};

const ComplexEntitySurface = ({
  children,
  icon,
  iconColor,
  surfaceTestId,
  testId,
  tooltip,
}: {
  children: ReactNode;
  icon: TableEntityIcon;
  iconColor: string;
  surfaceTestId: string;
  testId: string;
  tooltip?: ReactNode;
}) =>
  wrapWithTooltip(
    <Box
      data-testid={surfaceTestId}
      style={{
        ...surfaceBaseStyle,
      }}
    >
      <Box style={iconBaseStyle}>
        {renderIcon({
          color: iconColor,
          icon,
          testId,
        })}
      </Box>
      <Box
        style={{
          alignItems: 'flex-start',
          display: 'inline-flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>,
    tooltip
  );

const ReservationEntity = ({
  icon,
  placeholder,
  tooltip,
  value,
}: ReservationEntityProps) => {
  if (!value?.title?.trim()) {
    return (
      <Placeholder value={placeholder || TABLE_PRIMITIVE_VALUE_PLACEHOLDER} />
    );
  }

  return (
    <ComplexEntitySurface
      icon={icon || getEntityIconDefinition('reservation')}
      iconColor="#16A34A"
      surfaceTestId="table-entity-reservation-surface"
      testId="table-entity-reservation-icon"
      tooltip={tooltip}
    >
      <EntityLabel color="#525252">{value.title}</EntityLabel>
      {value.dateRange ? <EntityMeta>{value.dateRange}</EntityMeta> : null}
    </ComplexEntitySurface>
  );
};

const TransactionEntity = ({
  icon,
  placeholder,
  tooltip,
  value,
}: TransactionEntityProps) => {
  const resolvedValue = value?.trim();

  if (!resolvedValue) {
    return (
      <Placeholder value={placeholder || TABLE_PRIMITIVE_VALUE_PLACEHOLDER} />
    );
  }

  return wrapWithTooltip(
    <Box style={surfaceBaseStyle}>
      <Box style={transactionIconBaseStyle}>
        {renderIcon({
          color: getEntityIconColor('transaction'),
          icon: icon || getEntityIconDefinition('transaction'),
          style: transactionIconBaseStyle,
          testId: 'table-entity-transaction-icon',
        })}
      </Box>
      <EntityLabel color="#333333">{resolvedValue}</EntityLabel>
    </Box>,
    tooltip
  );
};

export const TableEntityValue = (props: TableEntityValueProps) => {
  if (props.type === 'transaction') {
    return <TransactionEntity {...props} />;
  }

  if (props.type === 'reservation') {
    return <ReservationEntity {...props} />;
  }

  return <SimpleEntity {...props} />;
};
